import { F0Alert, F0Box, F0Select, F0Text } from '@factorialco/f0-react'
import {
  AtUpdateUnassignedHiringPhaseStageDocument,
  AtUpdateUnassignedHiringPhaseStageMutation,
  AtsGetHiringPipelineStagesDocument,
  AtsGetHiringPipelinePhasesDocument,
  AtsUnassignedHiringPhaseFragment,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { UNASSIGNED_STAGE, editableStagesOptions } from 'modules/ats/lib/utils/hiringStages'

interface Props {
  unassignedPhases: AtsUnassignedHiringPhaseFragment[]
}
export const UnassignedHiringPhases = ({ unassignedPhases }: Props) => {
  const { data: hiringStagesData } = useQuery(AtsGetHiringPipelineStagesDocument)

  const hiringStages = hiringStagesData?.ats.hiringStagesConnection.nodes || []

  const [changeHiringStageAtsApplicationPhase] = useMutation(
    AtUpdateUnassignedHiringPhaseStageDocument,
    {
      extractorFn: (data: AtUpdateUnassignedHiringPhaseStageMutation) =>
        data.atsMutations.changeHiringStageAtsApplicationPhase,
      refetchQueries: [AtsGetHiringPipelinePhasesDocument],
    }
  )

  const onChange = async (id: string, atsHiringStageId: string) => {
    const result = await changeHiringStageAtsApplicationPhase({
      variables: {
        id,
        atsHiringStageId,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return handled
  }

  const getEditableApplicationPhases = (phases: AtsUnassignedHiringPhaseFragment[]) => {
    return phases.filter((phase) => phase.phaseType != 'initial' && phase.phaseType != 'hired')
  }

  const stagePhasesGroups = groupBy(
    getEditableApplicationPhases(unassignedPhases),
    (phase) => phase.hiringStage?.name || 'unassigned'
  )

  return (
    <F0Box paddingBottom="sm" gap="lg">
      <F0Alert
        variant="warning"
        title={i18n.t(
          'ats.job_posting_settings.hiring_pipeline.ununassigned_phases.warning_message'
        )}
        description=""
      />

      <F0Box border="default" borderColor="default" borderRadius="md">
        {stagePhasesGroups[UNASSIGNED_STAGE]?.map((item, index) => (
          <F0Box
            key={item.id}
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="between"
            padding="md"
            borderTop={index > 0 ? 'default' : undefined}
          >
            <F0Text variant="label" content={item.name} />
            <F0Box width="52">
              <F0Select
                label={i18n.t(
                  'ats.job_posting_settings.hiring_pipeline.ununassigned_phases.select_label'
                )}
                hideLabel
                name={`unassigned-phase-${item.id}`}
                placeholder={i18n.t(
                  'ats.job_posting_settings.hiring_pipeline.ununassigned_phases.select_label'
                )}
                size="md"
                value=""
                onChange={(value: string) => onChange(item.id, value)}
                options={editableStagesOptions(hiringStages)}
              />
            </F0Box>
          </F0Box>
        ))}
      </F0Box>
    </F0Box>
  )
}
